import {
  Box,
  FormControl,
  InputAdornment,
  styled,
  SxProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import React, { ChangeEvent, forwardRef, useMemo, useState } from "react";
import {
  Controller,
  FieldValues,
  RegisterOptions,
  UseControllerProps,
} from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { preprocessValueForTextField } from "util/preprocessValueForTextField";
import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";
import {
  SuggestedChip,
  SuggestedChipProps,
} from "components/ArtificialIntelligence/SuggestedChip";

export type FormTextFieldProps<T extends FieldValues> = {
  label: string | React.ReactNode;
  tooltip?: string;
  preserveEmptyString?: boolean;
  textFieldProps?: TextFieldProps;
  intOnly?: boolean;
  helperText?: string | React.JSX.Element;
  sx?: SxProps;
  fullWidth?: boolean;
  maxLength?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  InputProps?: TextFieldProps["InputProps"];
  InputLabelProps?: TextFieldProps["InputLabelProps"];
  rules?: RegisterOptions;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  hideErrors?: boolean;
  type?: React.HTMLInputTypeAttribute;
  show1Pass?: boolean;
  inputMask?: string | null;
  suggestionProps?: SuggestedChipProps;
} & Omit<UseControllerProps<T>, "name"> &
  (
    | { name: UseControllerProps<T>["name"]; readOnly?: never }
    | { name?: never; readOnly: true }
  );

const StyledTextField = styled(TextField)({
  "& .MuiInputLabel-root": {
    whiteSpace: "normal", // Allow wrapping
    overflow: "visible",
  },
  "& .MuiInputLabel-shrink": {
    whiteSpace: "nowrap",
  },
});

export const FormTextField = <T extends FieldValues>({
  name,
  label,
  tooltip,
  control,
  preserveEmptyString,
  textFieldProps,
  intOnly,
  readOnly,
  defaultValue,
  helperText,
  sx,
  fullWidth = true,
  maxLength,
  inputProps,
  InputProps,
  InputLabelProps,
  rules: rules,
  disabled,
  onBlur,
  hideErrors,
  type,
  show1Pass,
  inputMask,
  suggestionProps,
}: FormTextFieldProps<T>) => {
  const labelWithTooltip = useMemo(
    () =>
      tooltip ? <TextWithTooltip text={label} tooltip={tooltip} /> : label,
    [label, tooltip]
  );

  // This state keeps track of whether this field is focused
  const [inputFocus, setInputFocus] = useState(false);

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const displayValue =
      formContext?.getValues(name ?? "") ?? defaultValue ?? "";
    return (
      <DisplayOnlyField text={displayValue} label={labelWithTooltip ?? ""} />
    );
  }

  if (readOnly) {
    return (
      <FormControl fullWidth={fullWidth}>
        <TextField
          {...textFieldProps}
          value={defaultValue}
          disabled
          label={label}
          sx={sx}
          slotProps={{
            htmlInput: {
              "data-form-type": "other",
              ...(show1Pass ? {} : { "data-1p-ignore": true }),
              ...inputProps,
            },
          }}
        />
        {/* Displays error messages and helper text */}
        {/* NOTE: When hideErrors is true and helperText is undefined/null, ErrorDisplay
            returns null, which can cause spacing issues since FormHelperText normally
            provides vertical spacing. We default to " " (space) to preserve spacing. */}
        <ErrorDisplay helperText={helperText ?? " "} hideErrors />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth={fullWidth}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <StyledTextField
              key={name}
              {...textFieldProps}
              {...field}
              value={preprocessValueForTextField({
                value: field.value,
                defaultValue,
                onChange: field.onChange,
                removeLeading1: !!inputMask,
              })}
              name={name}
              label={labelWithTooltip}
              type={type}
              error={!!error}
              disabled={disabled}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                setInputFocus(false);
                onBlur?.(event);
              }}
              onFocus={() => setInputFocus(true)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (!intOnly || event.target.value === "") {
                  field.onChange(
                    !preserveEmptyString && event.target.value === ""
                      ? null
                      : event.target.value
                  );
                  return;
                }

                const value = event.target.value;
                const intValue = Number(value);
                if (!Number.isNaN(intValue)) {
                  field.onChange(Math.floor(intValue));
                }
              }}
              slotProps={{
                htmlInput: {
                  ...(maxLength ? { maxLength } : {}),
                  "data-form-type": "other",
                  ...(show1Pass ? {} : { "data-1p-ignore": true }),
                  ...(inputMask ? { format: inputMask, mask: "_" } : {}),
                  ...inputProps,
                },
                input: {
                  ...(inputMask
                    ? { inputComponent: PatternFormatWithRef }
                    : {}),
                  endAdornment: (() => {
                    const showCharCount =
                      inputFocus && maxLength && field.value;

                    if (!suggestionProps && !showCharCount) {
                      return null;
                    }

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <SuggestedChip label="Suggested" {...suggestionProps} />
                        {showCharCount && (
                          <InputAdornment position="end">
                            {`${field.value.length}/${maxLength}`}
                          </InputAdornment>
                        )}
                      </Box>
                    );
                  })(),
                  ...InputProps,
                },
                inputLabel: InputLabelProps,
              }}
            />
            {/* Displays error messages and helper text */}
            <ErrorDisplay
              error={error}
              helperText={helperText}
              hideErrors={hideErrors}
            />
          </>
        )}
      />
    </FormControl>
  );
};

// We need to forward the ref to the PatternFormat component so that the input can be focused
const PatternFormatWithRef = forwardRef<HTMLInputElement, any>((props, ref) => (
  <PatternFormat {...props} getInputRef={ref} format={props.format} />
));
PatternFormatWithRef.displayName = "PatternFormatWithRef";
